// src/pages/admin/Admin.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  StatCard,
  DateStatCard,
  MonthlyClinicSelection,
  Page,
  Tabs,
  useReportDate,
  usePersistedState,
  ForecastingSummaryBar,
  // DISABLED: Role Tester is no longer used — see the commented-out RoleTesterPanel below.
  // useRoleTest,
  // ROLE_TEST_META,
} from "@common";
import {
  PerformancePatients,
  Forecasts,
  Forecasting,
  ProfileDirectoryTabPanel,
  AuditLog,
  Downloads,
} from "@pages";
import { useOverviewClinicSummary, useClinics, useForecastsBatch } from "@api/hooks";
import { useAuth } from "@api/useAuth";
import Overview from "@pages/admin/Overview";
import {
  isManagerOrAbove,
  overviewSummaryStats,
  isOrganizerOrAdmin,
  isObserver,
  isClinicScopedUser,
  torontoDateParts,
} from "@utils";
import { clinicLabel } from "@utils/utils";
import AdminClinicProfile from "@pages/admin/AdminClinicProfile";
import LabInvoiceReviewPanel from "@pages/admin/LabInvoiceReviewPanel";
import InvisalignInvoiceReviewPanel from "@pages/admin/InvisalignInvoiceReviewPanel";

// DISABLED: Role Tester feature is no longer used. Kept commented out (rather than
// deleted) in case we want to bring it back. Everything from ROLE_COLOR_CLASSES
// through RoleTesterPanel below was only used by the "🔧 Role Tester" tab.
/*
const ROLE_COLOR_CLASSES = {
  indigo: {
    ring: "ring-palette-indigo-hover",
    bg: "bg-palette-indigo-soft",
    badge: "bg-palette-indigo-muted text-palette-indigo-heading",
    btn: "bg-palette-indigo-badge hover:bg-palette-indigo-heading text-white",
    exit: "bg-palette-indigo-muted hover:bg-palette-indigo-border text-palette-indigo-text",
  },
  violet: {
    ring: "ring-palette-violet-ring",
    bg: "bg-palette-violet-soft",
    badge: "bg-palette-violet-muted text-palette-violet-heading",
    btn: "bg-palette-violet-badge hover:bg-palette-violet-heading text-white",
    exit: "bg-palette-violet-muted hover:bg-palette-violet-border text-palette-violet-text",
  },
  purple: {
    ring: "ring-palette-purple-ring",
    bg: "bg-palette-purple-soft",
    badge: "bg-palette-purple-muted text-palette-purple-heading",
    btn: "bg-palette-purple-badge hover:bg-palette-purple-heading text-white",
    exit: "bg-palette-purple-muted hover:bg-palette-purple-border text-palette-purple-text",
  },
  gray: {
    ring: "ring-palette-gray-border",
    bg: "bg-palette-gray-soft",
    badge: "bg-palette-gray-subtle text-palette-gray-heading",
    btn: "bg-palette-gray-secondary hover:bg-palette-gray-badge text-white",
    exit: "bg-palette-gray-subtle hover:bg-palette-gray-divider text-palette-gray-text",
  },
  teal: {
    ring: "ring-palette-teal-ring",
    bg: "bg-palette-teal-soft",
    badge: "bg-palette-teal-muted text-palette-teal-heading",
    btn: "bg-palette-teal-badge hover:bg-palette-teal-heading text-white",
    exit: "bg-palette-teal-muted hover:bg-palette-teal-border text-palette-teal-text",
  },
  amber: {
    ring: "ring-palette-amber-ring",
    bg: "bg-palette-amber-soft",
    badge: "bg-palette-amber-muted text-palette-amber-heading",
    btn: "bg-palette-amber-label hover:bg-palette-amber-badge text-white",
    exit: "bg-palette-amber-muted hover:bg-palette-amber-border text-palette-amber-text",
  },
  orange: {
    ring: "ring-palette-orange-ring",
    bg: "bg-palette-orange-soft",
    badge: "bg-palette-orange-muted text-palette-orange-heading",
    btn: "bg-palette-orange-label hover:bg-palette-orange-badge text-white",
    exit: "bg-palette-orange-muted hover:bg-palette-orange-border text-palette-orange-text",
  },
  emerald: {
    ring: "ring-emerald-300",
    bg: "bg-emerald-50",
    badge: "bg-emerald-100 text-emerald-700",
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white",
    exit: "bg-emerald-100 hover:bg-emerald-200 text-emerald-800",
  },
};

function pickRandom(arr, n) {
  const shuffled = [...arr].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, n);
}

function RoleTesterPanel({ testRoleId, setTestRoleId, realUser }) {
  const { data: allClinicsData } = useClinics({ limit: 10000, offset: 0 });
  const clinicCodes = (allClinicsData?.results ?? []).map((c) => c.clinic).filter(Boolean);

  const activeColor = testRoleId
    ? (ROLE_COLOR_CLASSES[ROLE_TEST_META[testRoleId]?.color] ?? ROLE_COLOR_CLASSES.gray)
    : null;

  function handleSwitch(meta) {
    let clinics = [];
    if (meta.role === 4) {
      clinics = pickRandom(clinicCodes, 1);
    } else if (meta.role === 3 || meta.role === 8) {
      clinics = pickRandom(clinicCodes, 3);
    }
    setTestRoleId(meta.role, clinics);
    window.location.href = "/";
  }

  function handleExit() {
    setTestRoleId(null);
    window.location.href = "/";
  }

  return (
    <div className="space-y-6 py-2">
      <div className="rounded-lg border border-palette-blue-border bg-palette-blue-soft px-4 py-3 text-sm text-palette-blue-text">
        <p className="font-semibold mb-1">Role Testing Mode</p>
        <p>
          Select a role below to temporarily view the app as that role would see it. Navigation,
          tabs, and action buttons will update instantly. The amber banner at the top lets you exit
          at any time. API calls still use your real credentials — only UI role checks are affected.
        </p>
      </div>

      {testRoleId && (
        <div
          className={`flex items-center justify-between rounded-lg border px-4 py-3 ${activeColor.ring} ${activeColor.bg}`}
        >
          <div>
            <span className="text-sm font-semibold">
              Currently testing as{" "}
              <span className={`rounded px-2 py-0.5 text-xs font-bold ${activeColor.badge}`}>
                {ROLE_TEST_META[testRoleId]?.label}
              </span>
            </span>
            <p className="text-xs text-palette-gray-secondary mt-0.5">
              {ROLE_TEST_META[testRoleId]?.desc}
            </p>
          </div>
          <button
            type="button"
            onClick={handleExit}
            className={`ml-4 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${activeColor.exit}`}
          >
            ✕ Exit Role Test
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.values(ROLE_TEST_META).map((meta) => {
          const colors = ROLE_COLOR_CLASSES[meta.color] ?? ROLE_COLOR_CLASSES.gray;
          const isActive = testRoleId === meta.role;
          const isMyRole = realUser?.role === meta.role;
          return (
            <div
              key={meta.role}
              className={`rounded-xl border-2 p-4 flex flex-col gap-3 transition ${
                isActive
                  ? `${colors.ring} ${colors.bg} shadow-md`
                  : "border-palette-gray-divider bg-white"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${colors.badge}`}>
                  {meta.label}
                </span>
                {isMyRole && (
                  <span className="rounded-full bg-palette-gray-subtle px-2 py-0.5 text-[10px] font-semibold text-palette-gray-secondary">
                    Your role
                  </span>
                )}
              </div>
              <p className="text-xs text-palette-gray-secondary leading-snug flex-1">{meta.desc}</p>
              {isActive ? (
                <button
                  type="button"
                  onClick={handleExit}
                  className={`w-full rounded-lg py-1.5 text-xs font-semibold transition ${colors.exit}`}
                >
                  ✕ Exit
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSwitch(meta)}
                  className={`w-full rounded-lg py-1.5 text-xs font-semibold transition ${colors.btn}`}
                >
                  Switch to {meta.label}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
*/

export default function Admin() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialUrlTab = searchParams.get("tab") || "overview";

  // Strip stale query params, but keep ?tab=... and the profile/forecast params.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const allowed = new Set(["tab", "profileClinic", "forecast_month"]);
    const hasOther = Array.from(params.keys()).some((k) => !allowed.has(k));
    if (hasOther) {
      const kept = new URLSearchParams();
      for (const k of allowed) {
        const v = params.get(k);
        if (v) kept.set(k, v);
      }
      const search = kept.toString();
      navigate(
        { pathname: window.location.pathname, search: search ? `?${search}` : "" },
        { replace: true },
      );
    }
  }, [navigate]);

  const { reportDate, setReportDate } = useReportDate();
  const [tab, setTab] = useState(initialUrlTab);
  // Lab vs Invisalign sub-tab under "Lab/Invisalign Invoices"; survives a refresh.
  const [invoiceKind, setInvoiceKind] = usePersistedState("admin:invoiceKind", "lab");

  // Persist tab in URL so refresh stays on the same tab
  useEffect(() => {
    const current = new URLSearchParams(window.location.search).get("tab");
    if (tab && tab !== current) {
      const next = new URLSearchParams(window.location.search);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // CHANGED: was useState("") — now persisted so the area manager filter survives a page refresh.
  const [forecastingAreaManagerFilter, setForecastingAreaManagerFilter] = usePersistedState(
    "admin:forecastingAreaManagerFilter",
    "",
  );
  // CHANGED: was useState([]) — now persisted so selected clinics survive a page refresh.
  // Key is _v3 to bust any stale [] stored by earlier versions of this component.
  const [forecastingClinicFilters, setForecastingClinicFilters] = usePersistedState(
    "admin:forecastingClinicFilters_v3",
    [],
  );
  const [clinicSummaries, setClinicSummaries] = useState({});
  // Shared calendar month for all clinic blocks in the multi-clinic forecasting view.
  // Seeded from ?forecast_month=YYYY-MM so a page refresh stays on the same month.
  const [forecastCalYear, setForecastCalYear] = useState(() => {
    const fromUrl = searchParams.get("forecast_month");
    if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl)) return Number(fromUrl.split("-")[0]);
    return torontoDateParts().year;
  });
  const [forecastCalMonth, setForecastCalMonth] = useState(() => {
    const fromUrl = searchParams.get("forecast_month");
    if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl)) return Number(fromUrl.split("-")[1]) - 1;
    return torontoDateParts().month - 1;
  });
  // YYYY-MM string derived from shared calendar state — used by the month picker stat card
  const forecastMonthValue = `${forecastCalYear}-${String(forecastCalMonth + 1).padStart(2, "0")}`;

  // Persist forecast month in URL while the Forecasting tab is active; remove on others.
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    const current = next.get("forecast_month");
    if (tab === "forecasting") {
      if (forecastMonthValue !== current) {
        next.set("forecast_month", forecastMonthValue);
        setSearchParams(next, { replace: true });
      }
    } else if (current) {
      next.delete("forecast_month");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, forecastMonthValue]);

  // Date range for the batch forecast fetch — recomputed whenever the shared calendar month changes
  const forecastBatchMM = String(forecastCalMonth + 1).padStart(2, "0");
  const forecastBatchDateFrom = `${forecastCalYear}-${forecastBatchMM}-01`;
  const forecastBatchDateTo = `${forecastCalYear}-${forecastBatchMM}-${String(new Date(forecastCalYear, forecastCalMonth + 1, 0).getDate()).padStart(2, "0")}`;

  // Whether the shared calendar is showing a month that has already fully completed.
  // Computed once here rather than inline in JSX to avoid recomputing the current date twice per render.
  const now = torontoDateParts();
  const forecastIsPastMonth =
    forecastCalYear < now.year ||
    (forecastCalYear === now.year && forecastCalMonth < now.month - 1);
  // Called by any Forecasting block when the user clicks ‹ or › on that calendar.
  // Updating forecastCalYear/Month here causes EVERY other block to sync via their
  // calYearOverride/calMonthOverride props, so all 40 calendars always show the same month.
  function handleForecastCalNav(year, month) {
    setForecastCalYear(year);
    setForecastCalMonth(month);
  }
  // Called by the Month picker stat card at the top of the Forecasting tab.
  // The picker gives us a "YYYY-MM" string; we split it into year + 0-indexed month
  // to match how forecastCalMonth is stored in state.
  function handleForecastMonthChange(monthStr) {
    if (!monthStr) return;
    const [yr, mo] = monthStr.split("-").map(Number);
    setForecastCalYear(yr);
    setForecastCalMonth(mo - 1); // DateStatCard uses 1-indexed month; state is 0-indexed
  }

  useEffect(() => {
    setPage(1);
  }, [reportDate]);

  const { user } = useAuth();
  // DISABLED: Role Tester is no longer used.
  // const { testRoleId, setTestRoleId } = useRoleTest();
  const role = user?.role;
  const isObserverUser = isObserver(user);
  // Org/Admin/Observer all see the broad admin layout.
  const isAdminOrOrg = isOrganizerOrAdmin(user);
  // DISABLED: Role Tester is no longer used.
  // Role tester tab: only the actual organizer/admin (real user, not test role) can see it.
  // const canSeeRoleTester = isOrganizerOrAdmin(realUser);
  const canSeeAdminScope = isAdminOrOrg || isObserverUser;
  const canSeeProfileTab = isManagerOrAbove(user) || isObserverUser;
  // Audit log: Org, Admin, Observer (matches backend can_view_audit_log)
  const canSeeAuditLog = canSeeAdminScope;

  const {
    data: clinicSummaryData,
    isLoading: isLoadingSummary,
    refetch: refetchClinicSummary,
  } = useOverviewClinicSummary({ date: reportDate }, { enablePolling: true });

  // The summary query lives here, above the tab switch, so it never remounts when
  // the user comes back to Overview — without this an explicit refetch, the table
  // would show whatever was cached when the page first loaded.
  useEffect(() => {
    if (tab === "overview") refetchClinicSummary();
  }, [tab, refetchClinicSummary]);

  const { data: clinicsData } = useClinics(
    { limit: 10000, offset: 0 },
    { enabled: canSeeAdminScope },
  );

  const clinics = useMemo(() => {
    const results = clinicSummaryData?.results || [];
    if (isClinicScopedUser(user) && user?.clinic?.length) {
      const allowed = new Set(user.clinic.map((c) => c.toUpperCase()));
      return results.filter((c) => allowed.has((c.clinic || "").toUpperCase()));
    }
    return results;
  }, [clinicSummaryData, user]);
  const clinicProfiles = useMemo(() => clinicsData?.results || [], [clinicsData]);
  const totalClinics = clinics.length;

  const { reportSubmissions, collectionSubmissions, forecastSubmissions } = useMemo(
    () => overviewSummaryStats(clinicSummaryData),
    [clinicSummaryData],
  );

  const areaManagerOptions = useMemo(() => {
    if (!canSeeAdminScope) return [];

    const seen = new Set();
    return clinicProfiles
      .map((clinic) => String(clinic.area_manager || "").trim())
      .filter(
        (manager) => manager && !seen.has(manager.toLowerCase()) && seen.add(manager.toLowerCase()),
      )
      .sort((a, b) => a.localeCompare(b))
      .map((manager) => ({ value: manager, label: manager }));
  }, [clinicProfiles, canSeeAdminScope]);

  const forecastingClinicOptions = useMemo(() => {
    let base = clinicProfiles;
    if (forecastingAreaManagerFilter) {
      base = base.filter(
        (c) => String(c.area_manager || "").trim() === forecastingAreaManagerFilter,
      );
    }
    return base
      .filter((c) => c.clinic)
      .map((c) => ({ value: c.clinic, label: clinicLabel(c) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clinicProfiles, forecastingAreaManagerFilter]);

  // CHANGED: replaces the old approach of storing all 40 clinic codes in state.
  // Empty selection = "All Clinics" — the dropdown shows "All Clinics" and the calendar
  // renders every clinic. A specific selection shows only the chosen clinic(s).
  const effectiveForecastingClinics =
    forecastingClinicFilters.length > 0
      ? forecastingClinicFilters
      : forecastingClinicOptions.map((o) => o.value);

  // MOVED: was earlier in the component before forecastingClinicOptions existed.
  // Now uses effectiveForecastingClinics so "All Clinics" triggers a full batch fetch.
  const { data: batchForecastsData } = useForecastsBatch(
    effectiveForecastingClinics,
    { date_from: forecastBatchDateFrom, date_to: forecastBatchDateTo },
    { enabled: effectiveForecastingClinics.length > 0 },
  );

  // CHANGED: keyed on effectiveForecastingClinics instead of forecastingClinicFilters
  // so stale summaries are pruned whether the user has an explicit selection or not.
  useEffect(() => {
    setClinicSummaries((prev) => {
      const activeSet = new Set(effectiveForecastingClinics);
      const hasStale = Object.keys(prev).some((k) => !activeSet.has(k));
      if (!hasStale) return prev;
      const next = {};
      for (const c of effectiveForecastingClinics) {
        if (prev[c]) next[c] = prev[c];
      }
      return next;
    });
  }, [effectiveForecastingClinics]); // eslint-disable-line react-hooks/exhaustive-deps

  // CHANGED: aggregates over effectiveForecastingClinics (includes all when nothing selected).
  const aggregatedForecastSummary = useMemo(() => {
    const active = effectiveForecastingClinics.map((c) => clinicSummaries[c]).filter(Boolean);
    return {
      mtd: active.reduce((s, x) => s + x.mtd, 0),
      forecastFromToday: active.reduce((s, x) => s + x.forecastFromToday, 0),
      monthEnd: active.reduce((s, x) => s + x.monthEnd, 0),
    };
  }, [clinicSummaries, effectiveForecastingClinics]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Page>
      <div className="mb-4">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {tab === "forecasting" && canSeeAdminScope ? (
            // Forecasting tab — Month + Area Manager + Clinic, same layout as Associate Fee
            <MonthlyClinicSelection
              months={[forecastMonthValue]}
              onMonthsChange={(months) => handleForecastMonthChange(months[0])}
              disableCurrentAndFuture={false}
              clinicOptions={forecastingClinicOptions}
              clinicValue={forecastingClinicFilters}
              onClinicChange={setForecastingClinicFilters}
              betweenSlot={
                <StatCard icon="👤" label="Area Manager">
                  <select
                    value={forecastingAreaManagerFilter}
                    onChange={(e) => {
                      setForecastingAreaManagerFilter(e.target.value);
                      setForecastingClinicFilters([]);
                    }}
                    className="mt-2 w-full rounded-xl border border-palette-gray-divider px-3 py-2 text-[15px] outline-none focus:ring-2 focus:ring-palette-indigo-label bg-white"
                    aria-label="Filter by area manager"
                  >
                    <option value="">All area managers</option>
                    {areaManagerOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </StatCard>
              }
            />
          ) : tab === "forecasting" ? (
            // Non-admin forecasting view — just the month picker
            <DateStatCard
              mode="month"
              label="Month"
              monthValue={forecastMonthValue}
              onChangeMonth={handleForecastMonthChange}
            />
          ) : (
            <DateStatCard label="Date" value={reportDate} onChange={setReportDate} />
          )}

          {/* Hide report submission cards on Forecasting */}
          {tab !== "forecasting" && (
            <>
              <StatCard
                icon="🏠"
                label="Production Report Submissions"
                value={`${reportSubmissions}/${totalClinics}`}
              />
              <StatCard
                icon="🐷"
                label="Collection Report Submissions"
                value={`${collectionSubmissions}/${totalClinics}`}
              />
              <StatCard
                icon="📈"
                label="Forecast Report Submissions"
                value={`${forecastSubmissions}/${totalClinics}`}
              />
            </>
          )}
        </div>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { key: "overview", label: "Overview" },
          ...(canSeeAuditLog ? [{ key: "labInvoices", label: "Lab/Invisalign Invoices" }] : []),
          { key: "performance", label: "Performance & Patients" },
          { key: "forecasts", label: "Forecasts" },
          { key: "forecasting", label: "Forecasting" },
          { key: "download", label: "Download" },
          ...(canSeeProfileTab ? [{ key: "profile", label: "Employee Profile" }] : []),
          { key: "clinicProfile", label: "Clinic Profile" },
          ...(canSeeAuditLog ? [{ key: "auditLog", label: "Tracking API" }] : []),
          // DISABLED: Role Tester is no longer used.
          // ...(canSeeRoleTester ? [{ key: "roleTest", label: "🔧 Role Tester" }] : []),
        ]}
      />

      {tab === "overview" && (
        <Overview clinics={clinics} isLoading={isLoadingSummary} date={reportDate} />
      )}

      {tab === "performance" && <PerformancePatients date={reportDate} />}

      {tab === "forecasts" && (
        <Forecasts
          date={reportDate}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPage(1);
            setPageSize(n);
          }}
        />
      )}

      {tab === "download" && <Downloads />}

      {/* CHANGED: condition uses effectiveForecastingClinics (always non-empty when options are loaded)
           instead of forecastingClinicFilters (which is [] when "All Clinics" is selected). */}
      {tab === "forecasting" &&
        (canSeeAdminScope && effectiveForecastingClinics.length > 0 ? (
          <div className="space-y-6 mt-2">
            <ForecastingSummaryBar
              mtd={aggregatedForecastSummary.mtd}
              forecastFromToday={aggregatedForecastSummary.forecastFromToday}
              monthEnd={aggregatedForecastSummary.monthEnd}
              // CHANGED: was forecastingClinicFilters.length — now reflects the true rendered count
              clinicCount={effectiveForecastingClinics.length}
              isPastMonth={forecastIsPastMonth}
            />
            {/* CHANGED: iterates effectiveForecastingClinics so all clinics render when nothing is selected */}
            {effectiveForecastingClinics.map((clinic) => {
              const option = forecastingClinicOptions.find((o) => o.value === clinic);
              return (
                <Forecasting
                  key={clinic}
                  clinicId={clinic}
                  hideClinicSelector
                  hideSummary
                  label={option?.label || clinic}
                  onSummaryChange={(s) => setClinicSummaries((prev) => ({ ...prev, [clinic]: s }))}
                  calYearOverride={forecastCalYear}
                  calMonthOverride={forecastCalMonth}
                  onCalMonthChange={handleForecastCalNav}
                  forecastsDataOverride={batchForecastsData?.[clinic] ?? []}
                />
              );
            })}
          </div>
        ) : (
          <Forecasting
            clinicId={null}
            date={reportDate}
            onDateChange={setReportDate}
            hideClinicSelector={canSeeAdminScope}
            calYearOverride={forecastCalYear}
            calMonthOverride={forecastCalMonth}
            onCalMonthChange={handleForecastCalNav}
          />
        ))}

      {tab === "profile" && canSeeProfileTab && (
        <ProfileDirectoryTabPanel clinics={clinics} role={role} />
      )}
      {tab === "clinicProfile" && (
        <AdminClinicProfile initialClinicCode={searchParams.get("profileClinic")} />
      )}
      {tab === "labInvoices" && canSeeAuditLog && (
        <>
          <Tabs
            value={invoiceKind}
            onChange={setInvoiceKind}
            items={[
              { key: "lab", label: "Lab" },
              { key: "invisalign", label: "Invisalign" },
            ]}
          />
          {invoiceKind === "invisalign" ? (
            <InvisalignInvoiceReviewPanel canAssign={isAdminOrOrg} />
          ) : (
            <LabInvoiceReviewPanel canAssign={isAdminOrOrg} />
          )}
        </>
      )}
      {tab === "auditLog" && canSeeAuditLog && <AuditLog />}

      {/* DISABLED: Role Tester is no longer used.
      {tab === "roleTest" && canSeeRoleTester && (
        <RoleTesterPanel
          testRoleId={testRoleId}
          setTestRoleId={setTestRoleId}
          realUser={realUser}
        />
      )}
      */}
    </Page>
  );
}
